/**
 * @file AdminDashboard.jsx
 */
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Users,
  Stethoscope,
  CalendarDays,
  Clock3,
  Calendar as CalendarIconLucide,
  UserPlus,
  TrendingUp,
  TrendingDown,
  History,
  Bell,
  Settings,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useAdminDashboard } from "../hooks/useAdminDashboard";
import { ADMIN_STRINGS } from "../constants/strings";
import { Card, Chip } from "../ui";
import "./AdminDashboard.css";

function StatCard({ icon, label, value, trend, tone = "primary" }) {
  return (
    <Card hover className="adm-stat">
      <span className={`adm-stat-icon adm-stat-icon-${tone}`}>{icon}</span>
      <span className="adm-stat-value">{value ?? "-"}</span>
      <span className="adm-stat-label">{label}</span>
      {trend !== undefined && trend !== null && (
        <Chip tone={trend >= 0 ? "ok" : "err"} size="sm" leftIcon={trend >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}>
          {trend >= 0 ? "+" : ""}{trend}%
        </Chip>
      )}
    </Card>
  );
}

const formatChartDate = (dateStr) => {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
};

function PageSkeleton() {
  return (
    <div className="adm-page">
      <div className="sk-line sk-w-30 sk-h-32" />
      <div className="adm-stat-grid">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="sk-block sk-h-110" />
        ))}
      </div>
      <div className="adm-charts">
        <div className="sk-block sk-h-360" />
        <div className="sk-block sk-h-360" />
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { stats, isLoading } = useAdminDashboard();
  if (isLoading) return <PageSkeleton />;
  const s = stats || {};

  const chartLineProps = { stroke: "var(--primary)", strokeWidth: 2, dot: false };

  return (
    <div className="adm-page">
      <header className="adm-head">
        <span className="adm-eyebrow">Admin · Panel</span>
        <h1 className="adm-title">{ADMIN_STRINGS.DASHBOARD_TITLE}</h1>
      </header>

      <div className="adm-stat-grid">
        <StatCard icon={<Clock3 size={18} />} label={ADMIN_STRINGS.APPOINTMENTS_TODAY} value={s.appointmentsToday} tone="primary" />
        <StatCard icon={<UserPlus size={18} />} label={ADMIN_STRINGS.USERS_THIS_WEEK} value={s.growth?.usersThisWeek} trend={s.growth?.growthPercent} tone="ok" />
        <StatCard icon={<Users size={18} />} label={ADMIN_STRINGS.TOTAL_PATIENTS} value={s.usersByRole?.pacientes} tone="primary" />
        <StatCard icon={<Stethoscope size={18} />} label={ADMIN_STRINGS.TOTAL_PROFESSIONALS} value={s.usersByRole?.profesionales} tone="primary" />
        <StatCard icon={<CalendarDays size={18} />} label={ADMIN_STRINGS.TOTAL_APPOINTMENTS} value={s.appointments?.total} tone="primary" />
        <StatCard icon={<Clock3 size={18} />} label={ADMIN_STRINGS.PENDING} value={s.appointments?.pending} tone="warn" />
      </div>

      <div className="adm-charts">
        <Card>
          <h3 className="adm-chart-title">{ADMIN_STRINGS.SIGNUPS_CHART}</h3>
          {s.signupTrend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={s.signupTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                <XAxis dataKey="date" tickFormatter={formatChartDate} fontSize={11} stroke="var(--ink-3)" />
                <YAxis allowDecimals={false} fontSize={11} stroke="var(--ink-3)" />
                <Tooltip labelFormatter={formatChartDate} contentStyle={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 8 }} />
                <Line type="monotone" dataKey="count" {...chartLineProps} name="Registros" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="adm-no-data">{ADMIN_STRINGS.NO_TREND_DATA}</p>
          )}
        </Card>
        <Card>
          <h3 className="adm-chart-title">{ADMIN_STRINGS.APPOINTMENTS_CHART}</h3>
          {s.appointmentTrend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={s.appointmentTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                <XAxis dataKey="date" tickFormatter={formatChartDate} fontSize={11} stroke="var(--ink-3)" />
                <YAxis allowDecimals={false} fontSize={11} stroke="var(--ink-3)" />
                <Tooltip labelFormatter={formatChartDate} contentStyle={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 8 }} />
                <Line type="monotone" dataKey="count" stroke="var(--accent)" strokeWidth={2} dot={false} name="Citas" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="adm-no-data">{ADMIN_STRINGS.NO_TREND_DATA}</p>
          )}
        </Card>
      </div>

      <div className="adm-bottom">
        <Card>
          <h3 className="adm-chart-title">{ADMIN_STRINGS.RECENT_SIGNUPS}</h3>
          {s.recentSignups?.length > 0 ? (
            <ul className="adm-signup-list">
              {s.recentSignups.map((u) => (
                <li key={u.id} className="adm-signup">
                  <div>
                    <div className="adm-signup-name">{u.firstName} {u.lastName}</div>
                    <div className="adm-signup-meta">
                      {new Date(u.createdAt).toLocaleDateString("es-MX", {
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                  <Chip size="sm" tone="neutral">
                    {ADMIN_STRINGS.ROLE_LABELS[u.role] || u.role}
                  </Chip>
                </li>
              ))}
            </ul>
          ) : (
            <p className="adm-no-data">Sin registros recientes</p>
          )}
        </Card>

        <Card>
          <h3 className="adm-chart-title">{ADMIN_STRINGS.QUICK_LINKS}</h3>
          <div className="adm-quick-grid">
            {[
              { label: ADMIN_STRINGS.NAV_USERS, to: "/cx/usuarios", Icon: Users },
              { label: ADMIN_STRINGS.NAV_APPOINTMENTS, to: "/cx/citas", Icon: CalendarDays },
              { label: ADMIN_STRINGS.NAV_PROFESSIONALS, to: "/cx/profesionales", Icon: Stethoscope },
              { label: ADMIN_STRINGS.NAV_AUDIT, to: "/cx/auditoria", Icon: History },
              { label: ADMIN_STRINGS.NAV_NOTIFICATIONS, to: "/cx/notificaciones", Icon: Bell },
              { label: ADMIN_STRINGS.NAV_SETTINGS, to: "/cx/configuracion", Icon: Settings },
            ].map((l) => {
              const Icon = l.Icon;
              return (
                <RouterLink key={l.to} to={l.to} className="adm-quick">
                  <Icon size={16} />
                  <span>{l.label}</span>
                </RouterLink>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
